import React, { useState, useEffect } from "react";
import AddKids from "../../components/buttons/addKids";
import KidsList from "./KidsList";
import * as XLSX from "xlsx";
import { bulkCreateStudents } from "../../API/kids-mgmt";
import { getCourses } from "../../API/courses";
import { getBatches } from "../../API/Batches";
import { ERROR_TOAST, SUCCESS_TOAST } from "../../helpers/toasters";
import "./kids-mngmt.css";
import SecondaryButton from "../../components/buttons/SecondaryButton";
import { useSelector } from "react-redux";

export default function KidsMgmt() {
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [batchMap, setBatchMap] = useState(new Map());
  const [courseMap, setCourseMap] = useState(new Map()); // Store course name -> ObjectId mapping
  const [availableBatches, setAvailableBatches] = useState<string[]>([]);
  const user = useSelector((state: any) => state.auth);
  useEffect(() => {
    const fetchCoursesAndBatches = async () => {
      try {
        // Fetch courses and batches concurrently
        const [courses, batches] = await Promise.all([
          getCourses(),
          getBatches()
        ]);

        // Create course name to ID map
        const newCourseMap = new Map(
          courses.map((course: any) => [course.name.trim(), course._id])
        );
        setCourseMap(newCourseMap);

        // Create batch name to ID map
        const newBatchMap = new Map();
        const newBatchNames: string[] = [];

        batches.forEach((batch: any) => {
          // Generate a unique batch name
          const batchName = batch.name ||
            `${batch.course?.name || 'Unknown'} Batch-${batch._id.slice(-4)}`;

          newBatchMap.set(batchName.trim(), batch._id);
          newBatchNames.push(batchName.trim());
        });

        setBatchMap(newBatchMap);
        setAvailableBatches(newBatchNames);
      } catch (error) {
        console.error("Comprehensive Fetch Error:", error);
      }
    };

    fetchCoursesAndBatches();
  }, []);
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  };

  const handleDownloadSample = () => {
    const sampleData = [
      ["name", "dob", "contact", "city", "parents_name", "gender", "school", "courses", "batches"],
      ["John Doe", "2018-02-11", "9876543210", "New York", "Jane Doe", "male", "ABC School", "Karate, Chess", "Batch-A, Batch-B"],
      ["Emma Smith", "2016-03-19", "8765432109", "Los Angeles", "Michael Smith", "female", "XYZ School", "Abacus, Rubik's Cube", "Batch-C"]
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Sheet");

    XLSX.writeFile(workbook, "kids_sample.xlsx");
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert("Please select a file first!");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        const payload = jsonData.map((kid: any) => {
          // More robust batch and course processing
          const inputBatches = kid.batches
            ? kid.batches.split(",").map((b: string) => b.trim())
            : [];

          const inputCourses = kid.courses
            ? kid.courses.split(",").map((c: string) => c.trim())
            : [];

          // Find valid batch IDs
          const batchIds = inputBatches
            .map((batchName: string) => {
              const matchedBatchId = batchMap.get(batchName);
              return matchedBatchId;
            })
            .filter((id: string | undefined) => id !== undefined);

          // Find valid course IDs
          const courseIds = inputCourses
            .map((courseName: string) => {
              const matchedCourseId = courseMap.get(courseName);
              return matchedCourseId;
            })
            .filter((id: string | undefined) => id !== undefined);

          return {
            name: kid.name || "",
            dob: kid.dob || "",
            contact: kid.contact || "",
            city: kid.city || "",
            parents_name: kid.parents_name || "",
            gender: kid.gender || "male",
            school: kid.school || "",
            courses: courseIds,
            batches: batchIds,
          };
        });

        const response = await bulkCreateStudents(payload);
        SUCCESS_TOAST("Kids added successfully!");
        setIsModalOpen(false);
        setSelectedFile(null);
      } catch (error) {
        console.error("Comprehensive Upload Error:", error);
      }
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  return (
    <>
      <div className="courseSetUp">
        <div className="topList">
          <h2 className="top-heading">Student Profile Management</h2>
          <div className="search-inp">
            <button type="submit">Search</button>
            <input
              className="dashboard-search"
              type="search"
              value={search}
              placeholder="Search Kids Profile..."
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="bulk-create-btn" onClick={() => setIsModalOpen(true)}>Upload Excel</button>
          <AddKids value="Add Student" />
        </div>
        <KidsList search={search} />
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Upload Students Data</h3>
            <input type="file" accept=".xlsx, .xls" onChange={handleFileSelect} />
            <SecondaryButton onClick={handleDownloadSample}>Download Sample Sheet</SecondaryButton>
            <div className="modal-actions">
              <button onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button onClick={handleUpload}>Create</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}